import type { CmmdOrgProviderPolicy, ProviderKind, ServerProvider } from "@cmmd-forge/contracts";

import {
  OFFERABLE_PROVIDER_KINDS,
  resolveExecutionOfferability,
} from "../../providerPickerAvailability";
import {
  selectProviderAvailability,
  type ModelPickerProviderAvailability,
} from "./modelPickerProviderAvailability";
import {
  isUnbuiltPickerProvider,
  providerKindForRail,
  resolvePublishedProviderAvailability,
  UNBUILT_PICKER_PROVIDERS,
  type PickerRailProvider,
  type ShellProviderAvailability,
} from "../../cmmd/providerAvailabilityJoin";

/**
 * Which rows the picker rail draws, and in which order.
 *
 * The rail used to be a hardcoded list of five provider kinds, so a provider
 * the Environment published had no button and a provider Forge shipped needed
 * a code change to appear. It is now the union of three things that each know
 * something the others do not: what the Environment published, what the
 * Provider Offer names, and what the product has on its roadmap.
 */

/**
 * Rail order. Not a membership list: a provider only draws a row if the payload,
 * the Offer or the roadmap named it. This fixes where that row sits, so the
 * rail does not reshuffle when an Environment goes to sleep and the picker
 * falls back to the last-known inventory.
 *
 * Anything the payload names that is missing here is appended in payload order,
 * so a provider added to the wire gets a button without being added to a list.
 */
const PICKER_RAIL_ORDER: ReadonlyArray<PickerRailProvider> = [
  "codex",
  "claudeAgent",
  "cmmd",
  "ollama",
  ...UNBUILT_PICKER_PROVIDERS,
];

/**
 * Provider kinds that share one product row.
 *
 * `apex` is CMMD's profile lane, not a second provider: it carries the same
 * policy key and the same display name, so drawing its snapshot as its own row
 * would put two buttons labelled CMMD next to each other.
 */
const PICKER_RAIL_ALIAS: Partial<Record<ProviderKind, ProviderKind>> = { apex: "cmmd" };

function railProviderFor(provider: ProviderKind): ProviderKind {
  return PICKER_RAIL_ALIAS[provider] ?? provider;
}

export function resolveModelPickerRailProviders(input: {
  readonly providers: ReadonlyArray<ServerProvider> | undefined;
  readonly policy: CmmdOrgProviderPolicy | null | undefined;
}): ReadonlyArray<PickerRailProvider> {
  const published = (input.providers ?? []).map((provider) => railProviderFor(provider.provider));
  const members = new Set<PickerRailProvider>([
    ...OFFERABLE_PROVIDER_KINDS,
    ...published,
    ...UNBUILT_PICKER_PROVIDERS,
  ]);
  const ordered = PICKER_RAIL_ORDER.filter((provider) => members.has(provider));
  // `members` already contains every published provider, so only the order
  // list has anything left to say about them.
  const appended = published.filter((provider) => !PICKER_RAIL_ORDER.includes(provider));
  return [...ordered, ...new Set(appended)];
}

export interface ModelPickerRailRow {
  readonly provider: PickerRailProvider;
  readonly availability: ShellProviderAvailability;
  readonly snapshot: ServerProvider | undefined;
}

/**
 * Every rail row, with the availability that will label it.
 *
 * A joined entry wins when the caller had one: only a surface that can reach
 * CMMD knows about the Offer and about a credential still being delivered.
 * Everything else falls back to what the payload alone can prove.
 */
export function resolveModelPickerRailRows(input: {
  readonly providers: ReadonlyArray<ServerProvider> | undefined;
  readonly policy: CmmdOrgProviderPolicy | null | undefined;
  readonly availability: ReadonlyArray<ModelPickerProviderAvailability> | undefined;
}): ReadonlyArray<ModelPickerRailRow> {
  const joined = selectProviderAvailability(input.availability);
  const snapshots = railProviderSnapshots(input.providers);
  const hasLiveInventory = input.providers !== undefined;
  return resolveModelPickerRailProviders(input).map((provider) => {
    const snapshot = snapshots.get(provider);
    // The roadmap outranks every other source, including a joined answer and
    // the wire. Cursor ships a real CLI, so an Environment publishes it as
    // Not Usable with no connection; Forge has not shipped Cursor, and telling
    // someone to connect a provider they cannot use is worse than saying the
    // product is not ready. Only this list may produce "Coming soon."
    const availability = isUnbuiltPickerProvider(provider)
      ? ({ state: "withheld", reason: "unbuilt" } as const)
      : (joined.get(provider) ??
        resolveRailProviderAvailability({
          provider,
          snapshot,
          hasLiveInventory,
          policy: input.policy,
        }));
    return { provider, snapshot, availability };
  });
}

/**
 * The snapshot that speaks for a rail row, once aliases are folded in.
 *
 * Exported because the draft composer builds its own availability list and
 * keyed snapshots on the raw provider, so an apex-only payload left the CMMD
 * row with nothing speaking for it on that path and a snapshot on this one.
 */
export function railProviderSnapshots(
  providers: ReadonlyArray<ServerProvider> | undefined,
): ReadonlyMap<PickerRailProvider, ServerProvider> {
  const byProvider = new Map<PickerRailProvider, ServerProvider>();
  for (const provider of providers ?? []) {
    const railProvider = railProviderFor(provider.provider);
    // First writer wins so an `apex` snapshot cannot displace CMMD's own.
    if (!byProvider.has(railProvider)) byProvider.set(railProvider, provider);
  }
  return byProvider;
}

/**
 * What the picker alone can prove about a row, when no joined availability
 * reached it.
 *
 * The full join needs CMMD's account authority, which only the surfaces that
 * fetch provider access can supply. The Offer is here because the picker
 * already holds the policy, and the rest is whatever the Environment itself
 * published. It never invents a credential in flight, because that is not
 * visible from here.
 *
 * Two questions, in order, because they have different authors: Forge and the
 * organization decide whether the row may be offered at all, and only then does
 * the Environment get to answer.
 */
function resolveRailProviderAvailability(input: {
  readonly provider: PickerRailProvider;
  readonly snapshot: ServerProvider | undefined;
  readonly hasLiveInventory: boolean;
  readonly policy: CmmdOrgProviderPolicy | null | undefined;
}): ShellProviderAvailability {
  return (
    railOfferAvailability(input.provider, input.policy) ??
    resolvePublishedProviderAvailability(input.snapshot, input.hasLiveInventory)
  );
}

/**
 * Unbuilt or Not Offered, or `null` when the row is the Environment's to answer.
 *
 * A row with no `ProviderKind` behind it can only ever be Unbuilt: nothing
 * publishes it and no policy key names it.
 */
function railOfferAvailability(
  provider: PickerRailProvider,
  policy: CmmdOrgProviderPolicy | null | undefined,
): ShellProviderAvailability | null {
  if (isUnbuiltPickerProvider(provider)) return { state: "withheld", reason: "unbuilt" };
  const providerKind = providerKindForRail(provider);
  if (providerKind === null) return { state: "withheld", reason: "unbuilt" };
  const offerability = resolveExecutionOfferability(providerKind, policy);
  if (offerability.offered) return null;
  // `policy-unavailable` is the absence of an answer, not an answer. The
  // in-thread picker mounts before the policy request lands and is handed
  // `undefined`, and "Your organization has not enabled Claude." sends a
  // person to an administrator over a request that is still in flight. Only
  // this fallback can see that state: the join reaches the same question with
  // a policy that has landed, or with `null`, which is the local-runtime
  // contract and offers everything.
  return offerability.reason === "policy-unavailable"
    ? { state: "unresolved", cause: "error" }
    : { state: "withheld", reason: "not-offered" };
}
